import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';

/**
 * #200056 — ô nhập "tiêu chí QC" có gợi ý các tiêu chí đã từng dùng.
 *
 * Cố ý KHÔNG phải danh sách chọn cứng: người tạo task vẫn gõ tự do câu bất kỳ, gợi ý chỉ để đỡ
 * phải gõ lại. Chọn 1 gợi ý là ĐIỀN nguyên văn vào ô, sửa tiếp thoải mái.
 *
 * Gõ không dấu vẫn ra gợi ý có dấu (vd "ban" → "Bàn phải sạch") — cùng cách bỏ dấu với máy chủ.
 */

/** Bỏ dấu + hạ chữ thường để so khớp kiểu gõ không dấu. */
function boDau(text) {
  return (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/\s+/g, ' ')
    .trim();
}

const MAX_HIEN = 6;

export default function CriterionInputComponent({
  value,
  onChange,
  suggestions = [],
  placeholder,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const matched = useMemo(() => {
    const needle = boDau(value);
    const list = needle
      ? (suggestions || []).filter((s) => boDau(s?.name).includes(needle))
      : suggestions || [];
    // Gõ trùng khít 1 gợi ý rồi thì không mời chọn lại chính nó.
    return list.filter((s) => boDau(s?.name) !== needle).slice(0, MAX_HIEN);
  }, [suggestions, value]);

  // Bấm ra ngoài thì đóng danh sách gợi ý (mousedown để kịp trước blur của ô nhập).
  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('touchstart', onDocDown);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('touchstart', onDocDown);
    };
  }, [open]);

  const showList = open && matched.length > 0;

  return (
    <div ref={wrapRef} className="position-relative flex-grow-1">
      <Form.Control
        size="sm"
        type="text"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={testId}
        // Cỡ chữ trên điện thoại phải >= 16px, nếu không Safari iOS tự phóng to khi chạm vào ô.
        style={{ fontSize: 16 }}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      />

      {showList && (
        <div
          className="position-absolute w-100 bg-white border rounded shadow-sm"
          style={{ zIndex: 1080, top: '100%', left: 0, maxHeight: 220, overflowY: 'auto' }}
          data-testid={testId ? `${testId}-goi-y` : undefined}
        >
          <div className="px-2 pt-2 pb-1 text-muted" style={{ fontSize: 11 }}>
            Tiêu chí đã dùng trước đây
          </div>
          {matched.map((s) => (
            <button
              key={s.name}
              type="button"
              className="d-flex w-100 align-items-center gap-2 border-0 bg-transparent text-start px-2 py-2"
              style={{ fontSize: 13 }}
              // mousedown chứ không phải click: ô nhập mất focus trước khi click kịp bắn.
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(s.name);
                setOpen(false);
              }}
            >
              <span className="flex-grow-1 text-break">{s.name}</span>
              {typeof s.used_count === 'number' && s.used_count > 1 && (
                <span className="shrink-0 text-muted" style={{ fontSize: 11 }}>
                  đã dùng {s.used_count} lần
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
